import { useMemo, useState } from "react";
import { StateBlock } from "@/components/layout-2/ds";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { TimelineEntry } from "@/types/dialer/timeline";
import { formatParisDay, parisDateTime } from "@/utils/dialer-datetime";
import { isEngineEntry, TimelineItem } from "./timeline-item";

export type TimelineDayGroup = {
    day: string;
    label: string;
    entries: TimelineEntry[];
};

/**
 * Grouped by local day, newest day first and newest event first inside a day.
 * Callers pass entries in any order; encounter order is ignored in favour of
 * sequence so a page merge cannot scramble the journal.
 */
export function groupByDay(entries: TimelineEntry[]): TimelineDayGroup[] {
    const groups = new Map<string, TimelineDayGroup>();
    const ordered = [...entries].sort((left, right) => right.sequence - left.sequence);
    ordered.forEach(entry => {
        const local = parisDateTime(entry.at_local ?? entry.at);
        const day = local?.toISODate() ?? "unknown";
        if (!groups.has(day)) {
            groups.set(day, { day, label: local ? formatParisDay(entry.at_local ?? entry.at) : "Unknown date", entries: [] });
        }
        groups.get(day)?.entries.push(entry);
    });
    return [...groups.values()];
}

type CardTimelineProps = {
    entries: TimelineEntry[];
    /** Node category lookup, used to mirror the workflow canvas colours. */
    categoryByNodeId?: Record<string, string>;
    /** When set, only the entries of that node are shown, and the filter is undoable. */
    filterNodeId?: string | null;
    onClearNodeFilter?: () => void;
    selectedSequence?: number | null;
    onSelectEntry?: (entry: TimelineEntry) => void;
    /** More events after the window — newer than what is shown. */
    hasMore?: boolean;
    /** More events before the window — older history below the list. */
    hasPrevious?: boolean;
    /**
     * One flag per direction: auto-refresh keeps the forward fetcher busy most of the
     * time, and a shared flag would leave "Load older events" disabled throughout.
     */
    loadingNewer?: boolean;
    loadingOlder?: boolean;
    /** Forward page, from `last_sequence`. */
    onLoadNewer?: () => void;
    /** Backward page, from `first_sequence`. Absent on a screen with a whole journal. */
    onLoadOlder?: () => void;
    emptyMessage?: string;
    /** Sequences that just arrived at the top — soft-enter animation, no layout jump. */
    enteringSequences?: ReadonlySet<number>;
    /** Live polling of newer events; owned by the card journal. */
    autoRefresh?: boolean;
    onAutoRefreshChange?: (enabled: boolean) => void;
};

/**
 * The journal, readable by someone from the business: engine bookkeeping is hidden by
 * default and one switch brings it back. Shared by the card screen and the simulator.
 *
 * Newest events sit at the top — that is what an operator opens a card to see.
 */
export function CardTimeline({
    entries,
    categoryByNodeId,
    filterNodeId,
    onClearNodeFilter,
    selectedSequence,
    onSelectEntry,
    hasMore,
    hasPrevious,
    loadingNewer,
    loadingOlder,
    onLoadNewer,
    onLoadOlder,
    emptyMessage = "This card was just enrolled; nothing has happened yet.",
    enteringSequences,
    autoRefresh,
    onAutoRefreshChange
}: CardTimelineProps) {
    const [showEngine, setShowEngine] = useState(false);

    const visible = useMemo(() => {
        let result = entries;
        if (!showEngine) result = result.filter(entry => !isEngineEntry(entry));
        if (filterNodeId) result = result.filter(entry => entry.node_id === filterNodeId);
        return result;
    }, [entries, showEngine, filterNodeId]);

    const groups = useMemo(() => groupByDay(visible), [visible]);
    const hiddenCount = entries.length - visible.length;
    const showAutoRefresh = typeof autoRefresh === "boolean" && Boolean(onAutoRefreshChange);

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                {showAutoRefresh && (
                    <div className="flex items-center gap-2">
                        <Switch id="timeline-auto-refresh" checked={autoRefresh} onCheckedChange={onAutoRefreshChange} />
                        <Label htmlFor="timeline-auto-refresh" className="flex items-center gap-1.5 text-sm font-normal">
                            Auto-refresh
                            {autoRefresh && (
                                <span className="relative flex h-1.5 w-1.5" aria-hidden>
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-800 opacity-60" />
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-800" />
                                </span>
                            )}
                        </Label>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <Switch id="timeline-engine" checked={showEngine} onCheckedChange={setShowEngine} />
                    <Label htmlFor="timeline-engine" className="text-sm font-normal">
                        Show engine events
                    </Label>
                </div>
                {hiddenCount > 0 && <span className="text-xs text-gray-500">{hiddenCount} hidden</span>}
                {filterNodeId && (
                    <Button type="button" variant="outline" size="sm" className="h-7 bg-white" onClick={onClearNodeFilter}>
                        Clear node filter
                    </Button>
                )}
            </div>

            {/* Above the list: events newer than the window belong above when newest is on top.
                Hidden while auto-refresh is on — polling already pulls that page. */}
            {hasMore && onLoadNewer && !autoRefresh && (
                <Button type="button" variant="outline" size="sm" className="bg-white" onClick={onLoadNewer} disabled={loadingNewer}>
                    {loadingNewer ? "Loading…" : "Load newer events"}
                </Button>
            )}

            {visible.length === 0 ? (
                <StateBlock message={entries.length === 0 ? emptyMessage : "No event matches the current filters."} />
            ) : (
                <div className="space-y-4">
                    {groups.map(group => (
                        <div key={group.day} className="space-y-1">
                            <p className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">{group.label}</p>
                            <ul>
                                {group.entries.map(entry => (
                                    <TimelineItem
                                        key={entry.sequence}
                                        entry={entry}
                                        category={entry.node_id ? categoryByNodeId?.[entry.node_id] : undefined}
                                        selected={selectedSequence === entry.sequence}
                                        onSelect={onSelectEntry}
                                        entering={enteringSequences?.has(entry.sequence)}
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {/* Below the list: older history continues downward. */}
            {hasPrevious && onLoadOlder && (
                <Button type="button" variant="outline" size="sm" className="bg-white" onClick={onLoadOlder} disabled={loadingOlder}>
                    {loadingOlder ? "Loading…" : "Load older events"}
                </Button>
            )}
        </div>
    );
}
